import type { VanillaLinkProps } from '../Link/index.ts';
import clsx from 'clsx';
import { use, useCallback } from 'react';
import { TabsContext } from './TabsContext.ts';

export function Tab<TValue>(
  props: React.PropsWithChildren<{
    accessory?: React.ReactNode;
    count?: number;
    disabled?: boolean;
    renderLink?: (props: VanillaLinkProps) => React.ReactNode;
    selected?: boolean;
    value?: TValue;
  }>
): React.ReactNode {
  const { accessory, children, count, renderLink, value } = props;

  const { disabled: isParentDisabled, plain, setValue, value: currentValue } = use(TabsContext);

  const disabled = props.disabled || isParentDisabled;

  const selected = props.selected || (typeof value !== 'undefined' && value === currentValue);

  const borderClasses = selected
    ? disabled
      ? 'border-gray-400'
      : 'border-primary-500'
    : clsx('border-transparent', !disabled && 'hover:border-gray-300');

  const textClasses = selected
    ? disabled
      ? 'text-gray-500'
      : 'text-primary-600'
    : clsx('text-gray-500', !disabled && 'hover:text-gray-700');

  const handleClick = useCallback(() => {
    if (!selected) {
      if (typeof value !== 'undefined') {
        setValue(value);
      }
    }
  }, [setValue, selected, value]);

  const content = (
    <button
      className={clsx(
        textClasses,
        !accessory && borderClasses,
        disabled && 'pointer-events-none',
        plain ? 'py-1' : clsx('py-4', accessory ? 'pl-1' : 'border-b-2 px-1'),
        plain ? 'font-semibold' : 'font-medium',
        'flex text-sm whitespace-nowrap'
      )}
      disabled={disabled}
      onClick={handleClick}
      role="tab"
      type="button"
    >
      <div>{children}</div>

      {typeof count !== 'undefined' && count > 0 && (
        <span
          className={clsx(
            selected && !disabled ? 'bg-primary-100 text-primary-600' : 'bg-gray-100 text-gray-900',
            'ml-3 hidden rounded-full px-2.5 py-0.5 text-xs font-medium md:inline-block'
          )}
        >
          {count}
        </span>
      )}
    </button>
  );

  const tab = renderLink?.({ children: content, plain: true }) || content;

  return accessory ? (
    <div className={clsx('flex items-center', !plain && ['border-b-2 pr-1', borderClasses])}>
      {tab}
      <div className="ml-2 flex">{accessory}</div>
    </div>
  ) : (
    tab
  );
}
